import React, { useState, useRef, useEffect } from 'react'
import { useUIStore } from '@/stores/uiStore'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { useAuth } from '@/contexts/AuthContext'

export const UserMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { openModal: _openModal } = useUIStore()
  const { user, logout } = useAuth()

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Keyboard shortcut: CMD/Ctrl + Shift + Q for logout
  useEffect(() => {
    const handleKeyboard = async (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'q') {
        e.preventDefault()
        const confirmed = window.confirm('Sign out? (⌘⇧Q)')
        if (!confirmed) return

        try {
          await logout()
          navigate('/login')
        } catch {
          window.alert('Ricald could not remove this session from the device. Please try signing out again.')
        }
      }
    }

    document.addEventListener('keydown', handleKeyboard)
    return () => document.removeEventListener('keydown', handleKeyboard)
  }, [logout, navigate])

  const menuItems = [
    {
      label: 'Your Profile',
      icon: '👤',
      onClick: () => {
        navigate('/app/settings')
        setIsOpen(false)
      },
    },
    {
      label: 'Settings',
      icon: '⚙️',
      onClick: () => {
        navigate('/app/settings')
        setIsOpen(false)
      },
    },
    {
      label: 'Integrations',
      icon: '🔗',
      onClick: () => {
        navigate('/app/settings?tab=integrations')
        setIsOpen(false)
      },
    },
    {
      label: 'Help & Support',
      icon: '❓',
      onClick: () => {
        navigate('/app/help')
        setIsOpen(false)
      },
    },
    {
      type: 'divider',
    },
    {
      label: 'Sign Out',
      icon: '🚪',
      onClick: async () => {
        const confirmed = window.confirm('Are you sure you want to sign out?')
        if (!confirmed) return

        setIsOpen(false)
        try {
          await logout()
          navigate('/login')
        } catch {
          window.alert('Ricald could not remove this session from the device. Please try signing out again.')
        }
      },
      variant: 'danger' as const,
    },
  ]

  return (
    <div ref={menuRef} className="relative">
      {/* Menu button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'flex items-center justify-center h-10 w-10',
          'rounded-full bg-[var(--bg-card)] border border-[var(--border)]',
          'hover:bg-[var(--bg-hover)] transition-all',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/40'
        )}
        aria-label="User menu (⌘⇧Q to sign out)"
      >
        <span className="text-lg">👤</span>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className={clsx(
            'absolute right-0 mt-2 w-56 rounded-lg',
            'bg-[var(--bg-card)] border border-[var(--border)]',
            'shadow-[var(--shadow-lg)] z-[var(--z-dropdown)]',
            'animate-in fade-in slide-in-from-top-2 duration-200'
          )}
        >
          {/* Header */}
          <div className="p-4 border-b border-[var(--border)]">
            <p className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">Account</p>
            <p className="text-sm font-semibold text-[var(--text-primary)] mt-2">{user?.name || user?.email?.split('@')[0] || 'User'}</p>
            <p className="text-sm text-[var(--text-secondary)] mt-0.5">{user?.email ?? '—'}</p>
            <p className="text-xs text-[var(--text-muted)] mt-2">Active now</p>
          </div>

          {/* Menu items */}
          <div className="py-2">
            {menuItems.map((item, idx) => {
              if (item.type === 'divider') {
                return <div key={idx} className="my-2 border-t border-[var(--border)]" />
              }

              return (
                <button
                  key={idx}
                  onClick={item.onClick}
                  className={clsx(
                    'w-full px-4 py-3 text-base flex items-center gap-3',
                    'transition-colors text-left',
                    item.variant === 'danger'
                      ? 'text-[var(--status-error)] hover:bg-red-500/10 font-medium'
                      : 'text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
                  )}
                >
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default UserMenu
